// KPI UI saf mantığı + tek kopya kaynağı (M16 T9, RK36 — period-ui kalıbı).
// Bileşenler ince kalır; burada: seçim kalıcılığı (kural 19/K30), hydrate doğrulama, hücre
// görüntü biçimi, rozet sözlüğü. e2e spec'i (15-kpi-module) `KPI_COPY`'nin elle eşlenik kopyasını
// taşır — birini değiştirince diğerini de güncelle.

import {
  formatKpiNumber,
  isPartialKpiCell,
  type KpiLang,
  type KpiResolvedCell,
  type KpiWorkflowState,
  periodLabel,
} from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "./kpi-breakdown-copy";
import { KPI_PACKAGES_COPY } from "./kpi-packages-copy";
import { KPI_REORDER_COPY } from "./kpi-reorder-copy";
import { KPI_NOTES_COPY } from "./kpi-notes-copy";
import { KPI_TREE_COPY } from "./kpi-tree-copy";
import { KPI_UNIT_COPY } from "./kpi-unit-copy";
import { KPI_CELL_COPY, KPI_CELL_HISTORY_COPY } from "./kpi-cell-copy";
import { KPI_PORTAL_COPY } from "./kpi-portal-copy";
import {} from "./kpi-unit-options";

export const KPI_COPY = {
  kicker: "PERFORMANS GÖSTERGELERİ",
  /** Birim varyantı (DR1–DR10) — kaynak lib/kpi-unit-copy.ts (600 satır kuralı) */
  unit: KPI_UNIT_COPY,
  /** Kırılım hiyerarşisi (spec §6/§18) — kaynak lib/kpi-breakdown-copy.ts */
  breakdown: KPI_BREAKDOWN_COPY,
  /** Sektör paketleri (spec §5, DR6/DR12) — kaynak lib/kpi-packages-copy.ts */
  packages: KPI_PACKAGES_COPY,
  /** Sürükle-bırak sıralama (rev-2 #4b/#12b/#14) — kaynak lib/kpi-reorder-copy.ts */
  reorder: KPI_REORDER_COPY,
  /** KPI listeleri kategori ağacı (T7) — kaynak lib/kpi-tree-copy.ts */
  tree: KPI_TREE_COPY,
  /** Danışman Özet — kart kuralı + Dönem notu (2026-10-08 manuel test: "kartlar rastgele mi?") */
  ozetCards: {
    hint: "Özet kartları: Değişim kolonu açık ilk 4 gösterge (veri seti sırası). Değiştirmek için Veri Yönetimi › gösterge › Değişim kolonu.",
    none: "Özet kartı yok — Veri Yönetimi'nde bir göstergenin Değişim kolonu kutusunu açın (ilk 4'ü kart olur).",
    periodCompare: (prev: number) => `önceki yıl: ${prev}`,
    periodNoPrev: (prev: number) =>
      `önceki yıl (${prev}) Yıllar aralığında değil — karşılaştırma yok`,
  },
  titles: {
    ozet: "Özet",
    tablo: "Veri Girişi",
    import: "İçe Aktar",
    setler: "Veri Yönetimi",
    katalog: "Katalog",
  },
  emptyTitle: "Bir müşteri seçin",
  emptyBody:
    "Performans göstergeleri müşteri bazında tutulur. Seçiminiz hatırlanır; bir sonraki girişte son çalıştığınız müşteri ve veri seti otomatik açılır.",
  emptySearch: "Müşteri ara…",
  emptyNoSets: "Henüz veri seti yok",
  emptyOpen: "Aç →",
  emptyCreateSet: "＋ Katalogdan Veri Seti Oluştur",
  emptyHint:
    'İpucu: müşteri profilindeki "Performans Göstergeleri" sekmesinden doğrudan o müşterinin veri setine geçebilirsiniz.',
  noClientsMatch: "Eşleşen müşteri yok",
  clientLabel: "Müşteri",
  setLabel: "Veri Seti",
  notSelected: "Seçilmedi",
  noSetForClient: "Bu müşterinin veri seti yok",
  /** müşterinin seti var ama hiçbiri seçili değil (otomatik seçim başarısızsa) */
  selectSetPrompt: "Üst bardan bir veri seti seçin.",
  createSetCta: "Katalogdan Veri Seti Oluştur",
  /** Finar rev-A 4 — Veri Girişi üst barı: seçili veri setini Veri Yönetimi'nde aç */
  goToSet: "Veri Setine git",
  /** Finar rev-A 7 — veri seti editörü sütun başlığı (thStyle büyük harfe çevirir) */
  changeColumnToggle: "Değişim göster/gizle",
  /** Finar rev-B 24 — veri seti editörü yapışkan kaydet çubuğu + "N değişiklik" özeti */
  setChanges: {
    bar: "Kaydedilmemiş değişiklikler",
    count: (n: number): string => `${n.toString()} değişiklik`,
    listTitle: "Bekleyen değişiklikler",
    save: "Değişiklikleri kaydet",
    saving: "Kaydediliyor…",
    discard: "Vazgeç",
    cycle: (path: string): string => `Kaydedilemez — formül döngüsü: ${path}`,
    added: "eklendi",
    removed: "çıkarıldı",
    label: (a: string, b: string): string => `etiket: ${a} → ${b}`,
    unit: (a: string, b: string): string => `birim: ${a} → ${b}`,
    order: (a: number, b: number): string => `sıra: ${a.toString()} → ${b.toString()}`,
    parent: (a: string, b: string): string => `üst kategori: ${a} → ${b}`,
    root: "kök",
    formula: "formül değişti",
    activeFormula: "aktif formül değişti",
    changeOn: "Değişim kolonu: açıldı",
    changeOff: "Değişim kolonu: kapandı",
    clientAccess: (a: string, b: string): string => `müşteri erişimi: ${a} → ${b}`,
    aggregation: (a: string, b: string): string => `konsolide kuralı: ${a} → ${b}`,
    weight: "ağırlık göstergesi değişti",
    question: (a: string, b: string): string => `bağlı form sorusu: ${a} → ${b}`,
    increase: (a: string, b: string): string => `artış yönü: ${a} → ${b}`,
    direction: (v: boolean | null): string => (v === null ? "nötr" : v ? "↑ iyi" : "↓ iyi"),
    anomaly: (a: string, b: string): string => `anomali eşiği: ${a} → ${b}`,
    footnote: "dipnot değişti",
    catalogDefault: "katalog varsayılanı",
    none: "—",
  },
  /** Katalog UX K2 (2026-11-03) — satır sonu ⋯ menüsü (Kırılıma dönüştür / Kullanımdan kaldır) */
  catalogRowMenu: {
    button: "⋯",
    aria: (label: string): string => `${label} — satır işlemleri`,
  },
  /** Finar rev-B 10 — katalog satırından hızlı "Kullanımdan kaldır" (ConfirmDialog) */
  catalogDeprecate: {
    action: "Kullanımdan kaldır",
    aria: (label: string): string => `${label} — kullanımdan kaldır`,
    title: (label: string): string => `${label} kullanımdan kaldırılsın mı?`,
    body: "Değerleri korunur, yeni veri setlerine eklenemez. Hiç değeri ve alt göstergesi yoksa katalogdan silinir.",
    confirm: "Kullanımdan kaldır",
    deprecated: (n: number): string => `Kullanımdan kaldırıldı (${n.toString()} değer korunur)`,
    deleted: "Silindi",
    dismiss: "Kapat",
  },
  /** Finar rev-B 11 — 409 KPI_METRIC_IN_USE: göstergeyi içeren veri setleri (Veri Yönetimi bağlantısı) */
  metricInUse: {
    lead: "Gösterge şu veri setlerinde kullanılıyor; önce oradan çıkarın:",
    setLabel: (clientName: string, setName: string): string => `${clientName} · ${setName}`,
  },
  /** Finar rev-B 6 — veri seti editörü satır sonu düğmesi (IndicatorModal'ı açar) */
  editIndicator: "Düzenle",
  editIndicatorAria: (label: string): string => `${label} — düzenle`,
  /** Finar rev-A 8 — Katalogdan Veri Seti Oluştur ad alanı */
  setNameLabel: "Veri seti adı",
  yearsLabel: "Yıllar",
  cell: KPI_CELL_COPY,
  /** M17 — hücre "Geçmiş" sekmesindeki sürüm listesi (yalnız iç aktör; portalda liste yok) */
  cellHistory: KPI_CELL_HISTORY_COPY,
  // Finar rev. madde 17: "… bekliyor" → "Danışman onayında" (rozet büyük harf yazımı korunur)
  workflow: {
    submitted: "DANIŞMAN ONAYINDA",
    clarification: "AÇIKLAMA İSTENDİ",
    verified: "ONAYLANDI",
    // TODO-KPI-10 — şirket yöneticisi onayladı, danışman onayı hâlâ gerekir
    client_approved: "ŞİRKET ONAYLADI · danışman onayında",
  } satisfies Partial<Record<KpiWorkflowState, string>>,
  /** Danışman kuyruğu (TODO-KPI-10/14) */
  queue: {
    clarificationAsked: "Açıklama istendi",
    outsideYears: (ys: number[]) =>
      `Yıllar aralığının dışında danışman onayında değer var (${ys.join(", ")}) — bu satırlar listede görünmüyor.`,
    /** boş kuyruk — "· son onay <tarih>" bileşende eklenir */
    empty: "✓ Danışman onayında değer yok",
    extendYears: "Aralığı genişlet",
    clientApprovedBadge: "Şirket onaylı",
    clientApprovedFilter: "Yalnız şirket onaylı",
    variantImpactEmpty: "hesaplanamadı",
  },
  /** Müşteri portalı (T19, RK36 tek kaynak). "AI üretimi"/"AI Kontrolü" ifadesi burada GEÇMEZ. */
  portal: KPI_PORTAL_COPY,
  // K27 — çok işletmeli müşteride Yıllar görünümü
  yearsEntityLabel: "İşletme",
  yearsEntityConsolidated: "Konsolide (yalnız görüntüleme)",
  consolidatedReadonlyHint:
    "Konsolide hücre düzenlenemez — üst çubuktan bir işletme seçin ya da İşletmeler görünümüne geçin.",
  /** Kapsamlı müşteri: kapsam toplamı sütunu/seçeneği salt okunur (Y6, §12/3) */
  scopeTotalReadonlyHint: KPI_NOTES_COPY.scopeTotalReadonlyHint,
  /** Hücre notu iş parçacığı (danışman yüzeyi) — Dalga 3 */
  notes: KPI_NOTES_COPY.internal,
  contextLockTitle: "Kaydedilmemiş değişiklikler var",
  contextLockBody:
    "Müşteri ya da veri setini değiştirirseniz kaydedilmemiş değişiklikler kaybolur.",
  contextLockConfirm: "Değiştir",
  /** KpiScreen — müşterinin seti yokken üst bar ipucu */
  createSetFromBarHint: "Üst bardan katalogdan veri seti oluşturabilirsiniz.",
  /** K11 (Finar 4.6) — dipnot numaraları (grid üst simge + tablo altı liste; portal ortak) */
  footnotes: {
    title: "Dipnotlar",
    markLabel: (n: number) => `Dipnot ${String(n)}`,
  },
  // ─── güvenlik/UX taraması 2026-10-08 — RK36: satır içi grid/toolbar/durum kopyası tek kaynağa ─
  /** Veri Girişi toolbar'ı + KpiGridRow (danışman ve portal ortak; "AI" ifadesi geçmez) */
  grid: {
    loading: "Tablo yükleniyor…",
    refreshing: "Yenileniyor…",
    loadFailed: "Tablo yüklenemedi",
    retry: "Tekrar dene",
    search: "Gösterge ara",
    searchPlaceholder: "Gösterge ara…",
    categoryLabel: "Kategori",
    categoryAll: "Kategori: Tümü",
    expandAll: "Tümünü gör",
    collapseAll: "Tümünü kapat",
    viewLabel: "Görünüm",
    viewYears: "Yıllar",
    viewEntities: "İşletmeler",
    yearLabel: "Yıl",
    saving: "Kaydediliyor…",
    saved: "✓ Kaydedildi",
    /** P3-18: tek birim — HÜCRE sayar (yıl aralığı × işletme), gösterge değil */
    fillCounter: (f: {
      filled: number;
      missing: number;
      awaitingVerification?: number;
      calcError?: number;
    }) =>
      `${f.filled} hücre girildi · ${f.missing} boş` +
      (f.awaitingVerification ? ` · ${f.awaitingVerification} danışman onayında` : "") +
      (f.calcError ? ` · ${f.calcError} hesaplanamadı` : ""),
    /** kaydedilemeyen hücreler: durum metni + iki eylem (yazılanı yeniden gönder / sunucuyu yükle) */
    failedCells: (n: number) => `${n} hücre kaydedilemedi`,
    retryFailed: "Tekrar dene",
    loadServerValues: "Sunucudaki değerleri yükle",
    cycle: (path: string) =>
      `Formül döngüsü: ${path} — ilgili göstergeler hesaplanamıyor; Veri Yönetimi'nden düzeltin.`,
    cyclePortal: (path: string) =>
      `Formül döngüsü: ${path} — ilgili göstergeler hesaplanamıyor; danışmanınıza iletin.`,
    footerOpen: (open: number) => `${open} kategori açık`,
    footerIndicators: (total: number) => `${total} gösterge`,
    footerHelp: "Hücreye tıkla → düzenle · Ok tuşları / Tab / Enter ile gezin ·",
    footerFx: "ƒx",
    footerFxHint: "hesaplanan (otomatik)",
    dotPending: "danışman onayında girdi",
    dotPartial: "kısmi toplam",
    bandStats: (s: { leaves: number; missing: number; pending: number; calcError: number }) =>
      `${s.leaves} gösterge` +
      (s.missing ? ` · ${s.missing} boş` : "") +
      (s.pending ? ` · ${s.pending} XXX` : "") +
      (s.calcError ? ` · ${s.calcError} hesaplanamadı` : ""),
  },
  /** Danışman stale-write (409 KPI_STALE_WRITE) — portal `stale*` kopyasının danışman karşılığı */
  stale: {
    title: "Bu hücre sunucuda değişti",
    body: "Başka bir kullanıcı bu değeri sizden sonra güncelledi; yazdığınız değer kaydedilmedi.",
    serverValue: (v: string) => `Sunucudaki değer: ${v}`,
    show: "Sunucudaki değeri göster",
    dismiss: "Kapat",
    inline: "Başka bir kullanıcı bu hücreyi güncelledi — yenileyin",
  },
  /** KpiModal kaydedilmemiş değişiklik kapısı (Esc / ✕ / arka plan) */
  unsavedClose: {
    title: "Kaydedilmemiş değişiklikler var — kapatılsın mı?",
    body: "Pencereyi kapatırsanız yaptığınız değişiklikler kaybolur.",
    confirm: "Kapat",
    cancel: "Düzenlemeye dön",
    /** ✕ düğmesi — modalların kendi "Kapat" düğmesiyle ad çakışmasın (e2e getByRole) */
    closeButton: "Pencereyi kapat",
  },
  /** Onay kuyruğu toplu onay kapısı (yalnız "Seçilileri onayla"; tekil ✓ doğrudan) */
  bulkApprove: {
    title: (n: number) => `${n} girdi onaylanacak`,
    body: "Onaylanan değerler rapora ve Excel'e sayı olarak gider; işlem geri alınamaz, değer yeniden yazılırsa tekrar onaya düşer.",
    confirm: "Onayla",
  },
  /** store hata metinleri (kpi-store / portal-kpi-store) */
  store: {
    loadFailed: "KPI verisi yüklenemedi",
    setsFailed: "Veri setleri yüklenemedi",
    saveFailed: "Kaydedilemedi",
  },
  /** Danışman Özet (KpiOzetPage) — sekme adları e2e spec 15 `COPY.ozetTabs` ile elle eşlenik */
  ozet: {
    loading: "Özet yükleniyor…",
    periodLabel: "Dönem",
    stripLabel: "Durum şeridi",
    tabs: {
      all: "Tümü",
      missing: "Eksik — XXX/boş",
      check: "Kontrol bekleyen",
      approval: "Onay bekleyen — müşteri girdileri",
      approvalWithVariants: (n: number) => `Onay bekleyen — müşteri girdileri (+${n} varyant)`,
    },
  },
  /** "Excel'e Aktar" düğmesi (KpiExportButton — eski yerel KPI_EXPORT_COPY) */
  export: {
    label: "⬇ Excel'e Aktar",
    busy: "Hazırlanıyor…",
    title: "Formüllü Excel çıktısı (hesaplanan göstergeler Excel formülü olarak gelir)",
    noSet: "Önce bir veri seti seçin",
    failed: "Excel çıktısı alınamadı",
    /** TODO-KPI-09 — çıktı dili seçici (yalnız gösterge etiketleri; çeviri yoksa Türkçe etiket). */
    langLabel: "Çıktı dili",
    langTitle: "Gösterge etiketlerinin dili (çevirisi olmayan gösterge Türkçe kalır)",
    langShort: { tr: "TR", en: "EN" } satisfies Record<KpiLang, string>,
  },
  // TODO-KPI-23 — türetilmiş (ƒx) hücrede raporlanan değer girişi + XXX raporlanan
  reported: {
    fieldLabel: "Raporlanan değer",
    hint: "Raporda yayımlanan değer; hesap yapılamıyorsa ya da sabitlenirse bu kullanılır.",
    placeholder: "ör. 4,91",
    current: (v: string) => `şu an: ${v}`,
    save: "Kaydet",
    saveXxx: "Değer bekleniyor (XXX)",
    saveXxxTitle: "Raporlanan değer henüz yok — hücre XXX gösterir, HESAPLANAMADI değil",
    invalid: "Sayı girin ya da XXX seçin",
    failed: "Raporlanan değer kaydedilemedi",
    pendingLabel: "Değer bekleniyor",
    pendingHint: "Raporlanan değer bekleniyor — hücre, Excel ve raporda XXX görünür.",
  },
  // TODO-KPI-32 — "Ağırlıklı ortalama" seçiliyken ağırlık göstergesi (katalog + set penceresi)
  aggregationWeight: {
    label: "Ağırlık göstergesi",
    hint: "Her işletmenin değeri bu göstergenin aynı yıldaki değeriyle tartılır (ör. üretim miktarı).",
    required: "Ağırlıklı ortalama için ağırlık göstergesi seçin",
    placeholder: "Gösterge seçin…",
    search: "Gösterge adı ya da kodu…",
    catalogDefault: "Katalog varsayılanı",
  },
  // TODO-KPI-29 — set çerçevesi (frameworks tablosundan; "Rapor kapsamı" sabit listesi kalktı)
  framework: {
    label: "Çerçeve",
    none: "Çerçevesiz / Diğer",
    notPublished: "(yayında değil)",
    loading: "Çerçeveler yükleniyor…",
    loadFailed: "Çerçeve listesi yüklenemedi",
    hint: "Veri setinin hangi raporlama çerçevesine ait olduğunu belirtir; proje açarken uyumlu veri setleri öne çıkar, raporda çerçeve adı yazılır.",
    /** Müşteri seçilmemişken varsayılan veri seti adı gövdesi ("Veri Seti 2026"; `defaultKpiSetName`). */
    defaultSetNameBase: "Veri Seti",
  },
  /** M17 T3 — Review "KPI değerleri değişti" bandı + bölüm listesi çipi (e2e 15 `COPY.review`) */
  review: {
    staleTitle: "KPI değerleri değişti",
    staleLead: (n: number) =>
      `Bu bölüm üretildikten sonra kullandığı ${n} değer değişti — metni yeniden üretin ya da rakamları doğrulayın.`,
    staleRow: (label: string, year: number, from: string, to: string, unit: string | null) =>
      `${label} · ${year}: ${from} → ${to}${unit ? ` ${unit}` : ""}`,
    staleLeadNoList:
      "Bu bölüm üretildikten sonra kullandığı değerlerin onay ya da kayıt durumu değişti — metni yeniden üretin ya da rakamları doğrulayın.",
    staleMore: (n: number) => `+${n} daha`,
    regenerate: "Yeniden üret",
    regenerateLocked: "Onaylı bölüm — önce projeyi yeniden açın.",
    regenerateFailed: "Yeniden üretim başlatılamadı — tekrar deneyin.",
    acknowledge: "Rakamları doğruladım",
    acknowledgeFailed: "Doğrulama kaydedilemedi — tekrar deneyin.",
    acknowledged: (name: string, date: string) => `Rakamlar doğrulandı · ${name} · ${date}`,
    chip: "KPI değişti",
    chipAria: "KPI değerleri değişti",
  },
} as const;

// ─── Yeni veri seti adı varsayılanı (Finar rev-A 8) ──────────────────────────

/**
 * "Katalogdan Veri Seti Oluştur" ad alanının varsayılanı: seçili müşterinin adı (kullanıcı
 * değiştirebilir). Müşteri henüz seçilmemişse eski kural: "<çerçeve adı | Veri Seti> <yıl>".
 */
export function defaultKpiSetName(args: {
  clientName: string | null | undefined;
  frameworkName: string | null | undefined;
  year: number;
}): string {
  const client = args.clientName?.trim();
  if (client) return client;
  const fw = args.frameworkName?.trim() ?? "";
  return `${fw === "" ? KPI_COPY.framework.defaultSetNameBase : fw} ${args.year}`;
}

// ─── Set etiketi (TODO-KPI-29) ───────────────────────────────────────────────

export interface KpiSetLabelInput {
  name: string;
  frameworkName?: string | null;
  indicatorCount: number;
}

/** Bağlam çubuğu / liste etiketi: "GRI 2025 · GRI · 12 gösterge" (çerçevesiz: ad · N gösterge). */
export function kpiSetLabel(set: KpiSetLabelInput): string {
  const fw = set.frameworkName?.trim();
  return [set.name, ...(fw ? [fw] : []), `${set.indicatorCount} gösterge`].join(" · ");
}

/** Boş durum müşteri satırı: "4 set · GRI, TSRS" (tekil çerçeve adları, ilk 3 + "…"; çerçevesiz set sayılmaz). */
export function kpiSetsSummaryLabel(
  setCount: number,
  sets: readonly { frameworkName?: string | null }[],
): string {
  const names: string[] = [];
  for (const s of sets) {
    const fw = s.frameworkName?.trim();
    if (fw && !names.includes(fw)) names.push(fw);
  }
  const head = `${setCount} veri seti`;
  if (names.length === 0) return head;
  const shown = names.slice(0, 3).join(", ");
  return `${head} · ${shown}${names.length > 3 ? "…" : ""}`;
}

// ─── Seçim kalıcılığı (kural 19 / K30) ───────────────────────────────────────

export const KPI_SELECTION_KEY_PREFIX = "kpi.selection";

export interface KpiSelection {
  clientId: string | null;
  setId: string | null;
  /** son seçilen yıl aralığı (kullanıcı bazında hatırlanır; yoksa veri temelli varsayılan) */
  years?: number[];
}

export function kpiSelectionKey(userId: string): string {
  return `${KPI_SELECTION_KEY_PREFIX}.${userId}`;
}

export function readKpiSelection(userId: string): KpiSelection {
  if (typeof window === "undefined") return { clientId: null, setId: null };
  try {
    const raw = window.localStorage.getItem(kpiSelectionKey(userId));
    if (!raw) return { clientId: null, setId: null };
    const parsed = JSON.parse(raw) as Partial<KpiSelection>;
    const years =
      Array.isArray(parsed.years) && parsed.years.every((y) => Number.isInteger(y))
        ? [...parsed.years].sort((a, b) => a - b)
        : undefined;
    return {
      clientId: typeof parsed.clientId === "string" ? parsed.clientId : null,
      setId: typeof parsed.setId === "string" ? parsed.setId : null,
      ...(years && years.length > 0 ? { years } : {}),
    };
  } catch {
    return { clientId: null, setId: null };
  }
}

export function writeKpiSelection(userId: string, sel: KpiSelection): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(kpiSelectionKey(userId), JSON.stringify(sel));
  } catch {
    // quota / private mode — seçim hatırlanmaz, akış kesilmez
  }
}

export function clearKpiSelection(userId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(kpiSelectionKey(userId));
  } catch {
    // sessiz
  }
}

/**
 * Müşteri seçilince set kararı (saf, tur bulgusu P2-2): set yoksa null; tercih edilen (son
 * kullanılan / query) set listedeyse o; aksi halde İLK set. Tek sette de aynı kural (ilk = tek).
 * "Bu müşterinin veri seti yok" mesajı yalnız gerçekten set yokken görünür.
 */
export function pickSetForClient(
  sets: readonly { id: string }[],
  preferredId?: string | null,
): string | null {
  if (sets.length === 0) return null;
  if (preferredId && sets.some((s) => s.id === preferredId)) return preferredId;
  return sets[0]!.id;
}

/**
 * Hydrate kararı (saf): query > localStorage; müşteri listede yoksa ya da set o müşteriye ait
 * değilse ilgili parça sessizce temizlenir. Set verilmemiş/geçersizse müşterinin ilk seti seçilir
 * (`pickSetForClient`); set hiç yoksa null.
 */
export function resolveKpiHydration(args: {
  query: { clientId?: string | null; setId?: string | null };
  stored: KpiSelection;
  clients: { id: string }[];
  setsByClient: Record<string, { id: string }[]>;
}): KpiSelection {
  const clientId = args.query.clientId ?? args.stored.clientId;
  if (!clientId || !args.clients.some((c) => c.id === clientId)) {
    return { clientId: null, setId: null };
  }
  const sets = args.setsByClient[clientId] ?? [];
  const wanted = args.query.clientId
    ? (args.query.setId ?? null)
    : (args.query.setId ?? args.stored.setId);
  return { clientId, setId: pickSetForClient(sets, wanted) };
}

/** Avatar baş harfleri (P3-32): parantezli ek düşer, harf dışı karakter atlanır ("Akçansa (efr)" → "A"). */
export function initialsOf(name: string): string {
  return name
    .replace(/\([^)]*\)/g, " ")
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter((w) => w.length > 0)
    .map((w) => w[0]!)
    .slice(0, 2)
    .join("")
    .toLocaleUpperCase("tr");
}

/**
 * Finar rev-A 4 — "Veri Setine git": Veri Yönetimi rotası seçili müşteri + veri setiyle
 * (`readKpiQuery` ile hydrate edilir; yeni sekmede açılınca da aynı seçim gelir).
 */
export function kpiSetManagementHref(clientId: string | null, setId: string | null): string {
  const q = new URLSearchParams();
  if (clientId) q.set("clientId", clientId);
  if (setId) q.set("setId", setId);
  const qs = q.toString();
  return qs ? `/clients/kpi/setler?${qs}` : "/clients/kpi/setler";
}

/** `?clientId&setId` query'sini okur; geçersiz uuid'ler yok sayılır. */
export function readKpiQuery(search: string): { clientId?: string; setId?: string } {
  const q = new URLSearchParams(search);
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const out: { clientId?: string; setId?: string } = {};
  const c = q.get("clientId");
  const s = q.get("setId");
  if (c && uuid.test(c)) out.clientId = c;
  if (s && uuid.test(s)) out.setId = s;
  return out;
}

// ─── Hücre görüntüsü ─────────────────────────────────────────────────────────

export type KpiCellTone = "value" | "pending" | "empty" | "error" | "text";

export interface KpiCellDisplay {
  text: string;
  tone: KpiCellTone;
  /** amber nokta — kısmi toplam / onay bekleyen girdi */
  dot?: boolean;
}

export function formatKpiCell(
  cell: KpiResolvedCell | undefined,
  opts: { percent?: boolean; decimals?: number } = {},
): KpiCellDisplay {
  if (!cell) return { text: KPI_COPY.cell.dash, tone: "empty" };
  switch (cell.status) {
    case "filled":
      if (typeof cell.value === "number") {
        return {
          text: formatKpiNumber(cell.value, {
            ...(opts.percent ? { percent: true } : {}),
            ...(opts.decimals !== undefined ? { decimals: opts.decimals } : {}),
          }),
          tone: "value",
          ...(isPartialKpiCell(cell) || cell.unverified ? { dot: true } : {}),
        };
      }
      return { text: cell.text ?? KPI_COPY.cell.dash, tone: "text" };
    case "pending_xxx":
      return { text: KPI_COPY.cell.pending, tone: "pending" };
    case "not_measured":
      return { text: cell.text ?? KPI_COPY.cell.notMeasured, tone: "text" };
    case "calc_error":
      return {
        text: cell.hasPending ? KPI_COPY.cell.pending : KPI_COPY.cell.calcError,
        tone: cell.hasPending ? "pending" : "error",
      };
    default:
      return { text: KPI_COPY.cell.dash, tone: "empty" };
  }
}

/** Grid için varsayılan yıl aralığı (kural 8): veri olan en son 4 yıl; yoksa cari yıl ve 3 öncesi. */
/**
 * Yıl penceresi varsayılanı — sunucu `defaultYears` ile AYNI kural (2026-10-08, manuel test):
 * pencere sonu = max(veri olan en büyük yıl, cari yıl), geriye `span` yıl. Veri yoksa takvim.
 * 2028'de veri 2026'da bitmişse 2025–2028; veri 2029'a yazılmışsa 2026–2029.
 */
export function defaultKpiYears(
  now = new Date().getFullYear(),
  span = 4,
  dataYears: readonly number[] = [],
): number[] {
  const max = dataYears.length > 0 ? Math.max(...dataYears, now) : now;
  const out: number[] = [];
  for (let y = max - span + 1; y <= max; y += 1) out.push(y);
  return out;
}

/**
 * Yıl seçicisinin seçenekleri: takvim penceresi (cari −9 … +1) ∪ veri yılları — eski bir veri yılı
 * (ör. 2016) takvim penceresinden düşse de seçilebilir kalır; gelecek yıl planlama için +1.
 */
export function kpiYearOptions(
  now = new Date().getFullYear(),
  dataYears: readonly number[] = [],
  selected: readonly number[] = [],
): number[] {
  const lo = Math.min(now - 9, ...dataYears, ...selected);
  const hi = Math.max(now + 1, ...dataYears, ...selected);
  const out: number[] = [];
  for (let y = lo; y <= hi; y += 1) out.push(y);
  return out;
}

/** M17 T5 — yıl başlığı/seçeneği: takvimde "2026", mali yılda "MY2026" (etiket tek yer: periodLabel). */
export const yearHeaderLabel = (y: number, startMonth = 1): string =>
  periodLabel(y, "annual", startMonth);
/** Bağlam çubuğu notu: "veri 2023–2025" / "veri yok". */
export function describeDataYears(dataYears: readonly number[]): string {
  if (dataYears.length === 0) return "veri yok";
  const lo = Math.min(...dataYears);
  const hi = Math.max(...dataYears);
  return lo === hi ? `veri ${lo}` : `veri ${lo}–${hi}`;
}
